import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Calendar, Clock } from "lucide-react";

const TIME_SLOTS = ["09:00", "11:00", "14:00", "16:00", "18:00"];

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const IMG_POS = {
  "one-on-one-mentoring": "50% 22%",
  "manifestation-workshop": "50% 68%",
};

export default function Bookings() {
  usePageMeta(
    "Book a Session — Tarot & Oracle Readings, 1:1 Mentoring | Just be you",
    "Book tarot & oracle readings (with recorded video clip), 1:1 mentoring, coaching deep dives and manifestation workshops with Mehjabin."
  );
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null);
  const { format, currency } = useCurrency();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    date: todayPlus(2), time_slot: TIME_SLOTS[0], intention: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/services").then((r) => {
    setServices(r.data);
    const slug = new URLSearchParams(window.location.search).get("service");
    if (slug) {
      const s = r.data.find((x) => x.slug === slug);
      if (s) {
        setSelected(s);
        if (s.event_date) setForm((f) => ({ ...f, date: s.event_date, time_slot: s.event_time || "20:00" }));
        setTimeout(() => document.querySelector('[data-testid="booking-form-container"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
      }
    }
  }); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const payId = searchParams.get("pay");
    if (!payId) return;
    setSearchParams({}, { replace: true });
    api.post("/bookings/checkout", { booking_id: payId, currency })
      .then(({ data }) => {
        openRazorpay(data, {
          onSuccess: () => toast.success("Payment received — your sitting is fully confirmed. ✨"),
          onFailure: () => toast.error("Payment not completed. You can retry anytime from your email link."),
        });
      })
      .catch(() => toast.error("We couldn't find that booking. Please reach out on WhatsApp."));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", { service_slug: selected.slug, ...form });
      const { data } = await api.post("/bookings/checkout", { booking_id: r.data.id, currency });
      openRazorpay(data, {
        onSuccess: () => {
          toast.success("Your sitting is reserved & paid. A confirmation has been sent to your inbox. ✨");
          setForm({ ...form, name: "", email: "", phone: "", intention: "" });
          setSelected(null);
          setSubmitting(false);
        },
        onFailure: (dismissed) => {
          toast.error(dismissed
            ? "Your sitting is reserved, but the payment wasn't completed. You can retry or reach out to complete it."
            : "Payment could not be completed. Please try again.");
          setSubmitting(false);
        },
      });
    } catch {
      toast.error("Something interrupted that. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="bookings-page">
      <div className="absolute top-40 -left-20 w-96 h-96 bg-sage/30 organic-blob blur-3xl -z-0 pointer-events-none" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 relative">
        <div className="md:col-span-7">
          <div className="overline mb-5">Bookings</div>
          <h1 className="font-serif text-5xl md:text-6xl text-lapis font-light leading-tight">
            Sit with me,<br /><em className="text-peach-deep">unhurriedly</em>.
          </h1>
        </div>
        <div className="md:col-span-5 text-ink/65 leading-relaxed">
          Each session is a small, deliberate room — held for you alone. Choose the
          shape that calls, and arrive however you are.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
        {services.map((s, i) => (
          <motion.button
            key={s.id} type="button"
            onClick={() => {
              setSelected(s);
              if (s.event_date) setForm((f) => ({ ...f, date: s.event_date, time_slot: s.event_time || "20:00" }));
            }}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.08 }}
            data-testid={`service-card-${s.slug}`}
            className={`text-left group relative overflow-hidden rounded-[2rem] border-2 transition-all duration-500 ${
              selected?.slug === s.slug ? "border-lapis ring-4 ring-lapis/15" : "border-transparent hover:border-lapis/30"
            }`}
          >
            <div className="aspect-[16/9] overflow-hidden relative">
              <img src={s.image} alt={s.title} style={{ objectPosition: IMG_POS[s.slug] || "50% 50%" }} className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-lapis-deep/70 via-lapis-deep/10 to-transparent" />
              <div className="absolute bottom-4 left-5 text-canvas">
                <div className="overline text-cream mb-1" style={{ color: "#f5efe3" }}>{s.duration_minutes} min</div>
                <div className="font-serif text-2xl">{s.title}</div>
              </div>
            </div>
            <div className="p-6 bg-white/80">
              <p className="text-sm text-ink/70 leading-relaxed">{s.description}</p>
              {s.seats_left != null && (
                <div className="mt-3 inline-flex items-center gap-1.5 bg-peach/20 border border-peach-deep/25 rounded-full px-3 py-1 text-xs font-semibold text-peach-deep" data-testid={`seats-left-${s.slug}`}>
                  ✦ {s.seats_left} of {s.seats_total} seats left
                </div>
              )}
              <div className="mt-4 flex items-center justify-between">
                <span className="font-serif text-2xl text-lapis">{format(s.price, s.price_inr)}</span>
                <span className="text-xs uppercase tracking-[0.18em] text-ink/60 group-hover:text-lapis font-semibold">
                  {selected?.slug === s.slug ? "Chosen ✓" : "Choose →"}
                </span>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      {selected && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="mt-16 glass-strong p-8 md:p-12 rounded-[2rem] relative" data-testid="booking-form-container">
          <div className="overline mb-3">Reserving</div>
          <h2 className="font-serif text-3xl text-lapis font-light mb-2">{selected.title}</h2>
          <p className="text-ink/60 text-sm mb-10">{selected.duration_minutes} minutes · {format(selected.price, selected.price_inr)}</p>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-testid="booking-form">
            <div>
              <label className="overline block mb-2">Your name</label>
              <input required className="eth-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name-input" />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input required type="email" className="eth-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email-input" />
            </div>
            <div>
              <label className="overline block mb-2">Phone (optional)</label>
              <input className="eth-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone-input" />
            </div>
            {selected.event_date ? (
              <div className="md:col-span-2">
                <label className="overline block mb-2 flex items-center gap-2"><Calendar size={12} /> When we gather</label>
                <div className="inline-flex items-center gap-2 bg-lapis text-canvas rounded-full px-6 py-3 text-sm font-medium tracking-wide" data-testid="booking-fixed-event">
                  <Clock size={14} /> {selected.event_label || `${selected.event_date} · ${selected.event_time}`}
                </div>
                <p className="mt-2 text-xs text-ink/55">One live sitting only — your seat is held the moment payment completes.</p>
              </div>
            ) : (
            <>
            <div>
              <label className="overline block mb-2 flex items-center gap-2"><Calendar size={12} /> Date</label>
              <input required type="date" className="eth-input" value={form.date} min={todayPlus(1)} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="booking-date-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-3 flex items-center gap-2"><Clock size={12} /> Time</label>
              <div className="flex flex-wrap gap-3">
                {TIME_SLOTS.map((t) => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, time_slot: t })} data-testid={`booking-slot-${t}`}
                    className={`px-5 py-2 rounded-full text-sm tracking-wide border-2 transition font-medium ${
                      form.time_slot === t ? "bg-lapis text-canvas border-lapis" : "border-lapis/25 text-ink/70 hover:border-lapis bg-white/60"
                    }`}>{t}</button>
                ))}
              </div>
            </div>
            </>
            )}
            <div className="md:col-span-2">
              <label className="overline block mb-2">What you're holding (optional)</label>
              <textarea rows={3} className="eth-input resize-none" placeholder="A sentence or two, only if it helps you arrive."
                value={form.intention} onChange={(e) => setForm({ ...form, intention: e.target.value })} data-testid="booking-intention-input" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="booking-submit-btn">
                {submitting ? "Reserving…" : "Reserve & pay"}
              </button>
              {selected?.price_inr >= 2500 && (
                <p className="mt-3 text-xs text-ink/60" data-testid="split-pay-note">
                  Prefer to pay in 2–3 parts?{" "}
                  <a href="https://wa.me/919707788827?text=Hi%20Mehjabin%2C%20I%27d%20love%20a%20split%20payment%20plan%20for%20a%20session%20%F0%9F%8C%99" target="_blank" rel="noopener noreferrer" className="underline text-lapis font-semibold">WhatsApp me</a>{" "}
                  — we'll arrange it, no pressure.
                </p>
              )}
            </div>
          </form>
        </motion.div>
      )}
    </div>
  );
}
